import './custom-control';
import './custom-popup';
import './custom-markers';
import './detailed-event';
import './custom-layer';
import '../utils/common-callback';

import {uploadFile, TYPE_OVERLAY_GEOMETRY, convertToLayer, getEventName, getActivityName, isDeepEqual, openOrFocus} from '../utils/utils';
import {COLOR} from '../utils/enum';
import * as api from '../api';
import translator from '../utils/translation';
import {toponimySearch} from '../utils/search';
import {coordinateSearch} from '../utils/search_coordinates';
import {displayEventDetails} from '../utils/event-detail';
import {logout} from '../login';
import {Storage, STORAGE_KEYS} from '../utils/store';
import {DATABASES} from '../core/database/datasources';
import {GendLocWebsockets} from '../core/websocket.engine';
import {WS_CALLBACK} from '../utils/websocket-callback';
import {TraccarClient} from '../traccar/traccar';
import {saveEvent} from "../api";

/**
 * Extension commune de la carte leaflet
 */
const baseMap = L.Map.extend({
    _currentMap: null,
    _eventBus: null,

    initialize: function (htmlId, app, options) {
        const unit = Storage.get(STORAGE_KEYS.USER)?.unit;
        const center = unit?.location ?? {coordinates: [5.851921962454281, 45.217991199394]};

        this._eventBus = new (L.Evented.extend({}))();
        this._app = app;

        L.Map.prototype.initialize.call(this, htmlId, {
            center: [center.coordinates[1], center.coordinates[0]],
            zoom: unit?.zoom ?? 10,
            doubleClickZoom: false,
            ...options
        });

        const traccarClient = new TraccarClient(this._app);
        traccarClient.connect();
        traccarClient.addTo(this);

        this._sidebar = L.control.sidebar({
            autopan: false,
            closeButton: true,
            container: 'sidebar',
            position: 'left',
        }).addTo(this);

        this.on('baselayerchange', (e) => this._currentMap = e.name);

        // Clic droit : ouverture de la vue 3D centrée sur le point cliqué
        this.on('contextmenu', (e) => this._open3DView(e.latlng));

        this._configureWS();

        // On charge les layers
        this._baseInitPromise = api.getSquadDetail().then(squad => {
            this._squad = squad;
            [baseLayers, overlays] = this._initLayers();

            const maps = baseLayers.reduce((acc, map) => ({...acc, ...map.layers}), {});
            const layerToAdd = maps[this._currentMap] ?? Object.values(maps)[0];

            if (layerToAdd) {
                this.addLayer(layerToAdd);
            }

            this._layerControl = L.Control.styledLayerControl(baseLayers, overlays, {
                container_width: "300px",
                exclusive: true
            });

            this.addControl(this._layerControl);

            this._initFooter();
        });
    },

    _initFooter: function () {
        const footer = document.querySelector('#map-footer');
        if (!footer) {
            return;
        }

        const hideClosedValue = Storage.get(STORAGE_KEYS.HIDE_CLOSED_EVENTS);
        const hideClosed = hideClosedValue === true || hideClosedValue === 'true';

        const userDetails = Storage.get(STORAGE_KEYS.USER_DETAILS);
        const version = window.VERSION?.version ?? '';
        const squadTitle = `${userDetails?.username ?? ''} - ${version}`;

        footer.innerHTML = `
            <div class="home-link" style="margin-right: 30px;">
                <i class="fa-solid fa-home" style="cursor: pointer" onclick="openOrFocus('/', 'Gendloc')"></i>
                <i class="fa-solid fa-table-columns" style="cursor: pointer; margin-left: 15px;" title="Tableau de bord des opérations du jour" onclick="openOrFocus('/dashboard', 'GendlocDashboard')"></i>
            </div>
            <div class="squad-code" title="${squadTitle}">${this._squad?.code ?? ''}</div>
            <div class="current-time"></div>
            <div class="switch" title="cacher événements cloturés">
                <label>
                    <input type="checkbox" id="hide-closed-checkbox" ${hideClosed ? 'checked' : ''}>
                    <span class="lever"></span>
                </label>
            </div>
            <div class="event-buttons"></div>
            
        `;

        const hideClosedCheckbox = footer.querySelector('#hide-closed-checkbox');
        if (hideClosedCheckbox) {
            hideClosedCheckbox.addEventListener('change', (e) => {
                Storage.store(STORAGE_KEYS.HIDE_CLOSED_EVENTS, e.target.checked, true);
                this._updateFooterEvents();
            });
        }

        this._updateFooterTime();
        setInterval(() => this._updateFooterTime(), 10000);

        const db = this._app.getDatabase(DATABASES.eventDb);
        this._updateFooterEvents();

        const refreshEvents = () => this._updateFooterEvents();
        db.event.on('updated', refreshEvents);
        db.event.on('added', refreshEvents);
        db.event.on('deleted', refreshEvents);
    },

    _updateFooterTime: function () {
        const timeElement = document.querySelector('#map-footer .current-time');
        if (timeElement) {
            const now = new Date();
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            timeElement.textContent = `${hours}:${minutes}`;
        }
    },

    _updateFooterEvents: async function () {
        const eventButtonsContainer = document.querySelector('#map-footer .event-buttons');
        if (!eventButtonsContainer) {
            return;
        }

        const hideClosedValue = Storage.get(STORAGE_KEYS.HIDE_CLOSED_EVENTS);
        const hideClosed = hideClosedValue === true || hideClosedValue === 'true';

        const db = this._app.getDatabase(DATABASES.eventDb);
        const events = (await db.event.getAll())
            .filter(e => e.properties?.is_owner || e.properties?.as_support_squad)
            .filter(e => {
                const isOpen = e.properties?.open ?? e.open ?? e.properties?.isOpen ?? e.isOpen ?? true;
                return !hideClosed || (isOpen && isOpen !== 'false');
            });

        eventButtonsContainer.innerHTML = events
            .map(event => {
                const props = event.properties || {};
                const localId = props.event_local_id || '';
                const activity = getActivityName(props.event_activity);
                const accident = props.event_accident || '';
                const com = (props.com || '').replace(/^\s*[^-]+ - /, '').toUpperCase().slice(0, 10);
                const lastStatus = props.last_status || event.last_status || '';
                const statusKey = lastStatus.split(' ')[0];
                const isOpen = props.open ?? event.open ?? props.isOpen ?? event.isOpen ?? true;
                const color = (isOpen && isOpen !== 'false') ? (COLOR[statusKey] ?? 'red') : 'grey';
                const eventId = event.id || event.properties?.id;
                const isActiveEvent = String(eventId) === String(this.eventId);

                const shadows = [];
                if (isActiveEvent) shadows.push('inset 0 0 0 4px black');
                if (props.as_support_squad === true) shadows.push('inset 0 0 0 2px blue');
                const boxShadow = shadows.length ? `box-shadow: ${shadows.join(', ')};` : '';

                const label = `${localId} - ${com}`;
                const lat = event.geometry?.coordinates?.[1] || '';
                const lng = event.geometry?.coordinates?.[0] || '';

                return `<div class="event-btn" title="${accident} - ${activity}" style="background-color: ${color}; ${boxShadow}" onclick="openOrFocus('/?event=${eventId}&lat=${lat}&lng=${lng}&zoom=15', '${event.id}')">${label}</div>`;
            })
            .join('');
    },

    _configureWS: function () {
        this._app.onWsMessage(GendLocWebsockets.notification.name, (message) => {
            WS_CALLBACK[message.type]?.(message.data, this._app);
        });

        const isEventDetails = this.eventId !== null && this.eventId !== undefined;
        const db = this._app.getDatabase(DATABASES.eventDb);

        const updateSmsUI = async () => {
            let smss = await db.sms.getAll();

            if (isEventDetails) {
                smss = smss.filter(sms => sms.event.id == this.eventId);
            }

            const smsList = document.querySelector('#sms-list');
            if (smsList) {
                smsList.innerHTML = smss
                    .sort((a, b) => b.id - a.id)
                    .map(sms => Handlebars.templates['sms-sum']({
                        message: sms,
                        isEventDetails
                    }))
                    .join('');
            }
        };

        db.sms.on('updated', updateSmsUI);
        db.sms.on('added', updateSmsUI);
        db.sms.on('deleted', updateSmsUI);
        db.sms.on('cleared', updateSmsUI);

        setTimeout(updateSmsUI, 0);
    },

    getCurrentMap() {
        return this._currentMap;
    },

    /**
     * Construit l'URL de la vue 3D itowns pour un point donné.
     * @param {*} latlng Coordonnées du point (clic droit)
     * @returns {string}
     */
    _build3DUrl: function (latlng) {
        const query = new URLSearchParams({
            lat: latlng.lat,
            lon: latlng.lng,
            z: 5000,
        });

        return `/map3D.html?${query.toString()}`;
    },

    /**
     * Ouvre la vue 3D centrée sur le point du clic droit.
     * Version commune (carte globale) : pas de drawattachements à transmettre.
     * @param {*} latlng Coordonnées du point (clic droit)
     */
    _open3DView: function (latlng) {
        window.open(this._build3DUrl(latlng), '_blank', 'noopener,noreferrer');
    },

    /**
     * Chargement des layers
     * @returns
     */
    _initLayers: function () {
        const baseLayers = this._prepareLayers(Storage.get(STORAGE_KEYS.MAP_SRV), true);
        const externalOverlays = this._prepareLayers(Storage.get(STORAGE_KEYS.OVERLAY_SRV).flat());
        const internalOverlayTypes = Storage.get(STORAGE_KEYS.OVERLAY_INT_TYPES);

        const types = internalOverlayTypes.map(type => {
            const name = translator.t(`overlay.type.${type.name}`);
            return {
                'groupName': type.group?.name ?? 'Autres',
                'layers': {[name]: L.internalOverlayLayer(type, this)}
            };
        });

        const overlays = externalOverlays;

        types.forEach(type => {
            const group = overlays.find(g => g.groupName === type.groupName);

            if (group) {
                group.layers = {...group.layers, ...type.layers};
            } else {
                overlays.push(type);
            }
        });

        return [baseLayers, overlays];
    },

    /**
     * Permet de générer une data structure des layers
     * utilisable par le layer control (regroupement des layers)
     * @param {*} layers Les layers à réarranger
     * @param {*} useNameAsDefault Boolean si true le nom affiché sera le nom du layer, si false la traduction
     * du type de layer est utilisé. False par défaut
     * @returns
     */
    _prepareLayers: function (layers, useNameAsDefault = false) {
        const transformedLayers = layers.map((layer) => {
            const translationKey = `overlay.type.${layer.name}`;
            const name = !translator.exists(translationKey) && useNameAsDefault
                ? layer.name
                : translator.t(translationKey);

            const layerObj = convertToLayer(layer);

            return {
                'groupName': layer.group?.name ?? 'Autres',
                'layers': {[name]: layerObj}
            };
        });

        const groups = [];
        transformedLayers.forEach(layer => {
            const group = groups.find(g => g.groupName === layer.groupName);

            if (group) {
                group.layers = {...group.layers, ...layer.layers};
            } else {
                groups.push(layer);
            }
        });

        return groups;
    },

    _addCommonPanel: async function () {
        const user = Storage.get(STORAGE_KEYS.USER_DETAILS);
        const groups = user?.groups ?? [];
        const canEditOverlay = groups.includes('niveau1') || groups.includes('niveau2');

        this._sidebar.addPanel({
                id: 'documentation',
                tab: '<i class="fa-solid fa-book"></i>',
                position: 'bottom',
                title: translator.t('documentation'),
                button: function (e) {
                    e.preventDefault();
                    window.open('/documentation/', '_blank', 'noopener,noreferrer');
                }
            });

        if (canEditOverlay) {
            this._sidebar.addPanel({
                id: 'air-obstacles',
                tab: '<i class="fa-solid fa-triangle-exclamation"></i>',
                position: 'bottom',
                title: 'Signalements obstacles aériens',
                button: function (e) {
                    e.preventDefault();
                    window.open('/signalement-obstacle-aerien', '_blank', 'noopener,noreferrer');
                }
            });
            this._sidebar.addPanel({
                id: 'settings',
                tab: '<i class="fa-solid fa-gear"></i>',
                position: 'bottom',
                title: translator.t('settings_overlays'),
                button: function (e) {
                    e.preventDefault();
                    window.open('/gestion/', '_blank', 'noopener,noreferrer');
                }
            });
        }

        this._sidebar.addPanel({
            id: 'logout',
            tab: '<i class="fa-solid fa-right-from-bracket"></i>',
            title: translator.t('logout'),
            position: 'bottom',
            button: function (e) {
                e.preventDefault();
                logout();
            }
        });

        this._sidebar.addPanel({
            id: 'new-event',
            tab: '<span style="color: red; font-size: 24px"><i class="fa-solid fa-star-of-life"></i></span>',
            position: 'top',
            title: translator.t('new_event'),
            button: async (e) => {
                e.preventDefault();

                if (!window.map) {
                    console.error('La carte n’est pas initialisée.');
                    return;
                }

                const eventName = '';
                const newWindow = window.open('', '_blank');

                try {
                    const center = window.map.getCenter();
                    const types = Storage.get(STORAGE_KEYS.EVENT_OVERLAY_TYPE) || [];
                    const eventType = types.find(t => t.name === 'event');
                    const features = [];

                    if (eventType) {
                        features.push({
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: [center.lng, center.lat]
                            },
                            properties: {
                                name: translator.t('event.overlay.type.event'),
                                type: eventType
                            }
                        });
                    }

                    const event = await saveEvent({
                        name: eventName,
                        description: eventName,
                        local_id: null,
                        features: features
                    });

                    const url = new URL(window.location.href);
                    url.search = new URLSearchParams({
                        event: event?.id,
                        map: this.getCurrentMap(),
                        zoom: 16,
                        lat: center.lat,
                        lng: center.lng
                    }).toString();

                    if (newWindow) {
                        newWindow.location.href = url.toString();
                        newWindow.name = event.id;
                        newWindow.focus();
                    }
                } catch (error) {
                    if (newWindow) {
                        newWindow.close();
                    }

                    console.error('Impossible de créer l’événement.', error);
                }
            }
        });

        this._sidebar.addPanel({
            id: 'search',
            tab: '<i class="fa-solid fa-magnifying-glass"></i>',
            position: 'top',
            title: translator.t('toponymSearch'),
            pane: Handlebars.templates['search']()
        });

        this._sidebar.addPanel({
            id: 'search-coordinates',
            tab: '<i class="fa-solid fa-location-dot"></i>',
            position: 'top',
            title: translator.t('coordinateSearch'),
            pane: Handlebars.templates['search-coordinates']()
        });

        const isEventDetails = this.eventId !== null && this.eventId !== undefined;
        const db = this._app.getDatabase(DATABASES.eventDb);
        const messageFormOptions = {};

        if (isEventDetails) {
            messageFormOptions.types = Storage.get(STORAGE_KEYS.MESSAGE_TYPES);
            messageFormOptions.langs = Storage.get(STORAGE_KEYS.MESSAGE_LANGS);
        }

        let smss = await db.sms.getAll();
        if (isEventDetails) {
            smss = smss.filter(sms => sms.event.id == this.eventId);
        }

        this._sidebar.addPanel({
            id: 'sms-status',
            tab: '<i class="fa-solid fa-comment-sms"></i>',
            position: 'top',
            title: translator.t('sms_status'),
            pane: Handlebars.templates['sms-status']({
                url: window.location.href,
                isEventDetails: isEventDetails,
                messages: smss.sort((a, b) => b.id - a.id),
                messageFormOptions,
                event: this.eventId,
            })
        });

        if (document.querySelector('#phone')) {
            window.initPhoneInput();
        }

        db.helicopter.getAll().then(helicopters => {
            const filteredHelicopters = helicopters
                .filter(helicopter => helicopter.code !== "TERRESTRE")
                .sort((a, b) => a.code.localeCompare(b.code, 'fr', { sensitivity: 'base' }));

            this._sidebar.addPanel({
                id: 'helico-status',
                tab: '<i class="fa-solid fa-helicopter"></i>',
                position: 'top',
                title: translator.t('helico_status'),
                pane: Handlebars.templates['helico-status']({filteredHelicopters})
            });

            const updateHelicoUI = () => db.helicopter.getAll().then(helicopters => {
                const filteredHelicopters = helicopters
                    .filter(helicopter => helicopter.code !== "TERRESTRE")
                    .sort((a, b) => a.code.localeCompare(b.code, 'fr', { sensitivity: 'base' }));

                const helicoPanel = document.querySelector('#helico-status .pane-content');
                if (helicoPanel) {
                    helicoPanel.innerHTML = Handlebars.templates['helico-status']({filteredHelicopters});
                }
            });

            db.helicopter.on('updated', updateHelicoUI);
            db.helicopter.on('added', updateHelicoUI);
            db.helicopter.on('deleted', updateHelicoUI);
            db.helicopter.on('cleared', updateHelicoUI);
        });

        this._sidebar.on('content', (event) => {
            if (event.id === 'search') {
                toponimySearch(this);
            }

            if (event.id === 'search-coordinates') {
                coordinateSearch(this);
            }
        });
    }
});

/**
 * Carte vue globale
 */
const gendlocMap = baseMap.extend({
    initialize: function (htmlId, app, options) {
        window.name = 'Gendloc';
        baseMap.prototype.initialize.call(this, htmlId, app, options);

        // Init de la base commune
        this._baseInitPromise.then(() => {
            this._addCommonPanel();

            const eventsOfTheDayLayer = L.geoJSON([], {
                pointToLayer: (event, latlng) => L.gendlocMarker('event', latlng, {properties: {id: event.id, ...event.properties}}),
            }).addTo(this);

            const eventsPartnersOfTheDayLayer = L.geoJSON([], {
                pointToLayer: (event, latlng) => L.gendlocMarker('event', latlng, {properties: {id: event.id, ...event.properties}}),
            });

            this._layerControl.addOverlay(eventsOfTheDayLayer, translator.t('events.ofTheDay'), {groupName: 'Divers'});
            this._layerControl.addOverlay(eventsPartnersOfTheDayLayer, translator.t('events.partnersOfTheDay'), {groupName: 'Divers'});
            this.addLayer(eventsOfTheDayLayer);

            // Chargement du layer "Evénement du jour"
            const db = this._app.getDatabase(DATABASES.eventDb);

            const refreshEvents = () => db.event.getAll().then(events => {
                eventsOfTheDayLayer.clearLayers();
                eventsPartnersOfTheDayLayer.clearLayers();

                const ownerOrSupportEvents = events.filter(e => e.properties?.is_owner || e.properties?.as_support_squad || e.properties?.as_helicopter_squad);
                const partnerEvents = events.filter(e => e.properties?.as_partner_squad);

                eventsOfTheDayLayer.addData(ownerOrSupportEvents);
                eventsPartnersOfTheDayLayer.addData(partnerEvents);
            });

            refreshEvents();

            db.event.on('updated', refreshEvents);
            db.event.on('added', refreshEvents);
            db.event.on('deleted', refreshEvents);

            // Chargement du layer "Evénement de l'année"
            const eventsOfTheYearLayer = L.lazyGeoJSON(`/events/`, {
                pointToLayer: (event, latlng) => L.gendlocMarker('event', latlng, {properties: {id: event.id, ...event.properties}})
            });

            this._layerControl.addOverlay(eventsOfTheYearLayer, translator.t('events.ofTheYear'), {groupName: 'Divers'});

            //L.createEventControl().addTo(this);
        });
    },
});

const eventMap = baseMap.extend({
    eventId: null,

    /**
     * Initialisation de la carte
     * @param {*} params Paramètres d'initialisation
     * @param {*} htmlId Id de la div de la carte
     * @param {*} options
     */
    initialize: function (params, htmlId, app, options) {
        this._currentMap = params.get('map');
        this.eventId = params.get('event');
        window.name = this.eventId;

        // Init base commune
        baseMap.prototype.initialize.call(this, htmlId, app, options);

        this._baseInitPromise.then(() => {
            // Chargement du détail d'un événement
            this._getEventDetails(params.get('event'), params.get('zoom'), params.get('lat'), params.get('lng'));
        });

        this._types = Storage.get(STORAGE_KEYS.EVENT_OVERLAY_TYPE)
            .map((type) => type.icon
                ? ({...type, icon: `${process.env.GENDLOC_BACKEND_URL}${type.icon}`})
                : type)
            .sort((a, b) => a.name.localeCompare(b.name));

        // Fonction utiliser après séléction du type d'overlay 
        // à dessiner
        window.enableDraw = (event, typeName) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();

            this._enableDraw(typeName);
        };

        if (this.event?.open ?? true) {
            // Initilialisation des controles
            this._initControls(this._types);
        }
    },

    /**
     * Ouvre la vue 3D centrée sur le point du clic droit et transmet
     * les drawattachements de l'événement (déposés en localStorage,
     * relus par map3D.html).
     * @param {*} latlng Coordonnées du point (clic droit)
     */
    _open3DView: function (latlng) {
        const key = `map3d-features:${this.eventId}`;
        const features = (this.event?.features ?? [])
            .filter(feature => feature?.geometry);

        if (this.eventId && features.length) {
            try {
                Storage.store(key, {type: 'FeatureCollection', features});
            } catch (e) {
                console.warn('Impossible de transmettre les drawattachements à la vue 3D', e);
            }
        } else {
            Storage.clear(key);
        }

        const query = new URLSearchParams({
            lat: latlng.lat,
            lon: latlng.lng,
            z: 5000,
        });

        if (this.eventId) {
            query.set('event', this.eventId);
        }

        window.open(`/map3D.html?${query.toString()}`, '_blank', 'noopener,noreferrer');
    },

    /**
     * Met à jour l'état de la carte et du pointeur
     * après que le type de l'overlay à ajouter a été sélectionné
     * @param {*} typeName
     */
    _enableDraw: function (typeName) {
        if (this.pm.globalDrawModeEnabled()) {
            this.pm.disableDraw();
        }

        const type = this._types.find(t => t.name === typeName);
        const options = {};
        this._typeToDraw = type;

        if (type.icon) {
            options.markerStyle = {
                icon: L.icon({
                    iconUrl: type.icon,
                    iconSize: [35, 35]
                })
            };
        }

        this.pm.enableDraw(
            TYPE_OVERLAY_GEOMETRY[type?.geomType?.toLowerCase() ?? 'point'],
            options
        );
    },

    /**
     * Initialise les controles de dessins
     * @param {*} types
     */
    _initControls(types) {
        this.pm.setLang("fr");
        this.pm.addControls({
            position: 'topleft',
            drawCircleMarker: false,
            rotateMode: false,
            drawText: false,
            editMode: false,
            dragMode: true,
            cutPolygon: false,
            drawCircle: false,
            drawRectangle: false,
            // On désactive les controles par défaut pour utiliser les notres
            drawMarker: false,
            drawPolyline: false,
            drawPolygon: false,
        });

        // Handler pour une touche pressée
        this.on('pm:keyevent', (e) => {
            if (this.pm.globalDrawModeEnabled() && e.event.key === 'Escape') {
                this.pm.disableDraw();
            }
        });

        this.on('pm:globaldragmodetoggled', (e) => {
            console.log('Global drag mode toggled:', e.enabled);
        });

        this.on('pm:globaleditmodetoggled', (e) => {
            console.log('Global edit mode toggled:', e.enabled);
        });

        // Handler overlay ajouté à la carte
        this.on('pm:create', (e) => this._eventBus.fire('pm:create', e));

        // Handler overlay supprimé de la carte
        this.on('pm:remove', (e) => this._eventBus.fire('pm:remove', e));

        // Handler overlay déplacé sur la carte
        this.on('pm:dragend', (e) => {
            this._eventBus.fire('pm:dragend', e);
        });

        // Ajout du control pour joindre un fichier à un événement
        this.pm.Toolbar.createCustomControl({
            name: 'add_file',
            block: 'custom',
            title: 'Ajouter un fichier',
            className: 'fa-solid fa-file-arrow-up fa-control',
            toggle: false,
            onClick: uploadFile(this, '.gpx,.geojson,.gml,.kml')
        });

        // Ajout des controles de dessins
        this.pm.Toolbar.createCustomControl(L.gendlocMakerControl({
            map: this,
            types: types.filter(t => t.geomType?.toLowerCase() === 'point' && t.name !== 'gendloc')
        }));

        this.pm.Toolbar.createCustomControl(L.gendlocLineControl({
            map: this,
            types: types.filter(t => t.geomType?.toLowerCase() === 'polyligne')
        }));

        this.pm.Toolbar.createCustomControl(L.gendlocPolygonControl({
            map: this,
            types: types.filter(t => t.geomType?.toLowerCase() === 'polygone')
        }));
    },

    /**
     * Récupère les infos d'un événement
     * @param {*} eventId Id de l'événement
     * @param {*} zoom Zoom à utiliser par défaut si pas d'overlay pour ajuster la vue de la carte
     * @param {*} lat lat à utiliser par défaut si pas d'overlay pour ajuster la vue de la carte
     * @param {*} lng lng à utiliser par défaut si pas d'overlay pour ajuster la vue de la carte
     */
    _getEventDetails: function (eventId, zoom, lat, lng) {
        if (lat && lng) {
            this.setView({lat, lng}, zoom ?? this.getMaxZoom());
        }

        const db = this._app.getDatabase(DATABASES.eventDb);
        db.detailed_event.delete(eventId);
        this._pendingDetailedEventUpdate = null;

        api.getEvent(eventId, this.getBounds()).then(event => {
            const layer = L.eventDetails(event, {eventBus: this._eventBus}).addTo(this);

            db.detailed_event.put(event);
            this.event = event;

            const displayId = event.local_id;
            const displayCom = event.com
                ?.replace(/^\s*\d{5}\s*-\s*/, '')
                .toUpperCase()
                .slice(0, 12);
            document.title = `${displayId} - ${displayCom ?? ''}`;

            this._renderEventDetails();
            // this._layerControl.addOverlay(layer, `Localisation`, {groupName: `Evénement ${displayId}`});
            this._eventLayer = layer;
            this._updateEventLayerControlTitle();

            //this._initMaker(); //demande utilisateur de ne pas forcer le placement d'un amrqueur event immédaitement (mais laisse la possibilité de le faire manuellement)

            const applyDetailedEventUpdate = (value) => {
                // console.debug(`[WS] Received detailed_event updated for key: ${key}`);
                // Comparer les événements en excluant updated_at et en normalisant l'ordre des features et listes
                //TODO:est ce que pas possible d'utiliser la taille en octets ?
                const {
                    updated_at: _u1,
                    features: f1,
                    message_attachements: m1,
                    attachements: a1,
                    support_units: s1,
                    pathologies: p1,
                    ...currentWithoutLists
                } = this.event || {};
                const {
                    updated_at: _u2,
                    features: f2,
                    message_attachements: m2,
                    attachements: a2,
                    support_units: s2,
                    pathologies: p2,
                    ...newWithoutLists
                } = value || {};

                // Trier les listes par ID pour éviter les faux positifs dus à l'ordre
                const sortedCurrent = {
                    ...currentWithoutLists,
                    features: [...(f1 || [])].sort((a, b) => (a.id || 0) - (b.id || 0)),
                    message_attachements: [...(m1 || [])].sort((a, b) => (a.id || 0) - (b.id || 0)),
                    attachements: [...(a1 || [])].sort((a, b) => (a.id || 0) - (b.id || 0)),
                    support_units: [...(s1 || [])].sort((a, b) => (a.squad || '').localeCompare(b.squad || '')),
                    pathologies: [...(p1 || [])].sort()
                };
                const sortedNew = {
                    ...newWithoutLists,
                    features: [...(f2 || [])].sort((a, b) => (a.id || 0) - (b.id || 0)),
                    message_attachements: [...(m2 || [])].sort((a, b) => (a.id || 0) - (b.id || 0)),
                    attachements: [...(a2 || [])].sort((a, b) => (a.id || 0) - (b.id || 0)),
                    support_units: [...(s2 || [])].sort((a, b) => (a.squad || '').localeCompare(b.squad || '')),
                    pathologies: [...(p2 || [])].sort()
                };

                const isEqual = isDeepEqual(sortedCurrent, sortedNew);

                // Toujours mettre à jour l'objet local pour qu'il soit le plus frais possible
                this.event = value;

                if (isEqual) {
                    return;
                }

                // Vérifier si seuls les éléments de tracking ont changé
                // On compare les objets sans les features
                const {features: _f1, ...currentWithoutFeatures} = sortedCurrent;
                const {features: _f2, ...newWithoutFeatures} = sortedNew;

                const isOnlyFeaturesChanged = isDeepEqual(currentWithoutFeatures, newWithoutFeatures);

                if (isOnlyFeaturesChanged) {
                    // Si seules les features ont changé, on vérifie si ce sont des traces de tracking
                    const currentTraces = (f1 || []).filter(f => f.properties?.type?.name === 'trace' || f.properties?.type === 12 || (f.geometry?.type === 'LineString' && f.properties?.name?.toLowerCase().includes('tracking')));
                    const newTraces = (f2 || []).filter(f => f.properties?.type?.name === 'trace' || f.properties?.type === 12 || (f.geometry?.type === 'LineString' && f.properties?.name?.toLowerCase().includes('tracking')));

                    const currentOtherFeatures = (f1 || []).filter(f => !(f.properties?.type?.name === 'trace' || f.properties?.type === 12 || (f.geometry?.type === 'LineString' && f.properties?.name?.toLowerCase().includes('tracking'))));
                    const newOtherFeatures = (f2 || []).filter(f => !(f.properties?.type?.name === 'trace' || f.properties?.type === 12 || (f.geometry?.type === 'LineString' && f.properties?.name?.toLowerCase().includes('tracking'))));

                    const isOtherFeaturesEqual = isDeepEqual(
                        currentOtherFeatures.sort((a, b) => (a.id || 0) - (b.id || 0)),
                        newOtherFeatures.sort((a, b) => (a.id || 0) - (b.id || 0))
                    );

                    if (isOtherFeaturesEqual) {
                        // Seules les traces ont changé (ou ont été ajoutées/supprimées)
                        layer.update(this.event);
                        // console.debug(`[WS] Tracking update only, map refreshed but sidebar kept intact.`);
                        return;
                    }
                }

                layer.update(this.event);
                this._renderEventDetails();

                // Mettre à jour le titre du document si l'événement a été mis à jour via websocket
                if (this.event.com) {
                    const displayId = this.event.local_id;
                    const displayCom = this.event.com
                        ?.replace(/^\s*\d{5}\s*-\s*/, '')
                        .toUpperCase()
                        .slice(0, 12);
                    document.title = `${displayId} - ${displayCom ?? ''}`;
                }
            };

            this._flushPendingDetailedEventUpdate = () => {
                if (!this._pendingDetailedEventUpdate) {
                    return;
                }

                const pendingValue = this._pendingDetailedEventUpdate;
                this._pendingDetailedEventUpdate = null;
                applyDetailedEventUpdate(pendingValue);
            };

            db.on('detailed_event:updated', ({key, value}) => {
                // console.debug(`[WS] Received detailed_event updated for key: ${key}`);
                // Ne traiter que les mises à jour de l'événement courant
                // Utiliser == au lieu de !== pour gérer les différences de type (string vs number)
                if (eventId != key) {
                    return;
                }

                // Mémoriser la dernière mise à jour reçue pendant une sauvegarde locale,
                // puis l'appliquer dès la fin de sauvegarde.
                if (this._isSavingEvent) {
                    this._pendingDetailedEventUpdate = value;
                    return;
                }

                applyDetailedEventUpdate(value);
            });
        });
    },

    /**
     * Initialize le mode dessin s'il n'y a pas déjà une point de type event
     * @returns
     */
    _initMaker() {
        if (!this.event?.features) {
            return;
        }

        const points = this.event.features.filter((feat) => {
            const typeName = feat.properties?.type?.name ?? feat.properties?.type;
            return typeName === 'event';
        });

        if (points.length > 0) {
            return;
        }

        const type = this._types.find(t => t.name === 'event');
        const options = {};

        if (type?.icon) {
            options.markerStyle = {
                icon: L.icon({
                    iconUrl: type.icon,
                    iconSize: [35, 35]
                })
            };
        }

        this._typeToDraw = type;
        this.pm.enableDraw('Marker', options);
    },

    _updateEventDetailsHeader: function () {
        const detailsPane = this._sidebar._panes.find(node => node.id === 'details');

        if (!detailsPane || !this.event) {
            return;
        }

        const eventName = getEventName(this.event);
        const headerNode = detailsPane.querySelector('.leaflet-sidebar-header');

        if (headerNode) {
            headerNode.textContent = eventName;
        }

        const detailsTab = this._sidebar._tabitems?.find(node => node._id === 'details');
        if (detailsTab?.querySelector) {
            const tooltipNode = detailsTab.querySelector('[title]');
            if (tooltipNode) {
                tooltipNode.setAttribute('title', eventName);
            }
        }
    },

    _updateEventLayerControlTitle: function () {
        if (!this._layerControl || !this._eventLayer || !this.event) {
            return;
        }

        const displayId = this.event.local_id;
        const overlayName = `Evénement ${displayId}`;
        const controlLayers = Object.values(this._layerControl._layers || {});
        const eventLayerEntry = controlLayers.find(({layer, overlay}) => (
            overlay && layer === this._eventLayer
        ));

        if (!eventLayerEntry) {
            return;
        }

        eventLayerEntry.name = overlayName;
        this._layerControl._update();
    },

    /**
     * Ajout un item dans le menu de gauche de la carte
     * correspondant aux détails de l'événement.
     *
     * @param {*} refresh
     */
    _renderEventDetails: function () {
        const db = this._app.getDatabase(DATABASES.eventDb);
        displayEventDetails(this.event, this, this._squad, db).then(template => {
            const refresh = !!this._sidebar._panes.find(node => node.id === 'details');

            if (!refresh) {
                this._sidebar.addPanel({
                    id: 'details',
                    tab: '<i class="fa-solid fa-circle-info"></i>',
                    pane: template,
                    title: getEventName(this.event),
                    position: 'top'
                });

                this._addCommonPanel();
                this._sidebar.open('details');
            } else {
                const contentNode = this._sidebar._panes.find(node => node.id === 'details');

                if (contentNode) {
                    const newContent = `<h1 class="leaflet-sidebar-header">${getEventName(this.event)}</h1> ${template}`;
                    contentNode.innerHTML = newContent;
                }
            }

            M.Modal.init(document.querySelectorAll('.modal'));

            if (document.querySelector('#phone') || document.querySelector('#phoneR')) {
                window.initPhoneInput();
                const phoneR = document.querySelector('#phoneR');

                if (phoneR) {
                    const styleId = 'phoneR-placeholder-style';
                    if (!document.getElementById(styleId)) {
                        const styleNode = document.createElement('style');
                        styleNode.id = styleId;
                        styleNode.textContent = `
                            #phoneR::placeholder { color: #d1d1d1 !important; opacity: 1; }
                            #phoneR::-webkit-input-placeholder { color: #d1d1d1 !important; opacity: 1; }
                            #phoneR::-moz-placeholder { color: #d1d1d1 !important; opacity: 1; }
                            #phoneR:-ms-input-placeholder { color: #d1d1d1 !important; opacity: 1; }
                        `;
                        document.head.appendChild(styleNode);
                    }

                }

            }

        });
    }
});

(() => {
    window.openOrFocus = openOrFocus;
    L.gendlocMap = (htmlId, app, options) => new gendlocMap(htmlId, app, options);
    L.eventMap = (params, htmlId, app, options) => new eventMap(params, htmlId, app, options);
})();